<script lang="ts" setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { Color } from '@previo/codebooks'
import { PSpaceSize } from '../space/types'
import LabelInputField from '../fields/components/LabelInputField.vue'
import PIcon from '../icon/PIcon.vue'
import PSpace from '../space/PSpace.vue'
import PCard from '../card/PCard.vue'
import PTextDescription from '../typography/system/PTextDescription.vue'
import { PCardRound, PCardSize } from '../card/types'
import PBtnTable from '../btn-table/PBtnTable.vue'
import { PFileType, PFileVariant } from './types'

type Props = {
  variant: PFileVariant
  label?: null | string
  tooltip?: null | string
  hint?: null | string
  required: boolean
  file: null | File
  url?: null | string
  isDeleteButtonVisible?: boolean
  isFullscreenButtonVisible?: boolean
  cy: string
  fileName?: null | string
  fileType?: null | PFileType
}

type Emits = {
  (e: 'deleteBtnClick', evt: Event): void
  (e: 'fullscreenBtnClick', evt: Event): void
}

const props = withDefaults(defineProps<Props>(), {
  variant: PFileVariant.SQUARE,
  label: null,
  tooltip: null,
  hint: null,
  required: false,
  file: null,
  url: null,
  isDeleteButtonVisible: false,
  isFullscreenButtonVisible: false,
  btnSecondIcon: null,
  fileName: null,
  fileType: null,
})

const emits = defineEmits<Emits>()

const imageObjectUrl = ref<string | null>(null)

const effectiveFileType = computed<PFileType | null>(() => {
  if (props.file) return getPreviewTypeFromFile(props.file)

  return props.fileType ?? null
})

const effectiveFileName = computed<string>(() => {
  if (props.file) return props.file.name

  return props.fileName ?? ''
})

const isImagePreview = computed<boolean>(() => effectiveFileType.value === PFileType.IMAGE)

const imageSrc = computed<string | null>(() => props.url ?? imageObjectUrl.value)

const iconByFileType = computed<string>(() => {
  switch (effectiveFileType.value) {
    case PFileType.IMAGE:
      return 'file-image'
    case PFileType.PDF:
      return 'file-pdf'
    case PFileType.CSV:
      return 'file-csv'
    case PFileType.JSON:
      return 'file-brackets-curly'
    case PFileType.XML:
      return 'file-xml'
    case PFileType.OTHER:
      return 'file'
    case PFileType.TXT:
      return 'file-text'
    case PFileType.WORD:
      return 'file-word'
    case PFileType.EXCEL:
      return 'file-excel'
  }

  return 'file'
})

const getPreviewTypeFromFile = function (file: File): PFileType | null {
  switch (file.type) {
    case 'application/pdf':
      return PFileType.PDF
    case 'text/csv':
      return PFileType.CSV
    case 'application/json':
      return PFileType.JSON
    case 'application/xml':
      return PFileType.XML
    case 'text/plain':
      return PFileType.TXT
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    case 'application/vnd.oasis.opendocument.text':
      return PFileType.WORD
    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    case 'application/vnd.oasis.opendocument.spreadsheet':
      return PFileType.EXCEL
    default: // Image or other types
      return file.type.startsWith('image/') ? PFileType.IMAGE : null
  }
}

watch(
  () => (props.url ? null : props.file && isImagePreview.value ? props.file : null),
  (file) => {
    if (imageObjectUrl.value) {
      URL.revokeObjectURL(imageObjectUrl.value)
      imageObjectUrl.value = null
    }
    if (file) imageObjectUrl.value = URL.createObjectURL(file)
  },
  { immediate: true },
)

onUnmounted(() => {
  if (imageObjectUrl.value) URL.revokeObjectURL(imageObjectUrl.value)
})
</script>

<template>
  <div class="ds-file-preview" :data-cy="cy">
    <label-input-field :label="label" :tooltip="tooltip" :is-required="required" />

    <p-card
      class="ds-file-preview__card p-transition-duration"
      :class="{
        'ds-file-preview__card--square': variant === PFileVariant.SQUARE,
        'ds-file-preview__card--fill': variant === PFileVariant.FILL,
        'ds-file-preview__card--has-image': !!imageSrc,
      }"
      :round="PCardRound.MD"
    >
      <div class="ds-file-preview__card-inner">
        <img
          v-if="imageSrc"
          :src="imageSrc"
          :alt="effectiveFileName"
          class="ds-file-preview__image"
        />

        <p-card
          v-if="isDeleteButtonVisible || isFullscreenButtonVisible"
          class="ds-file-preview__card--btns p-display-flex p-justify-end"
          :pa-mobile="PCardSize.SM"
        >
          <p-btn-table
            v-if="isFullscreenButtonVisible"
            icon="arrow-up-right-and-arrow-down-left-from-center"
            :color="Color.PRIMARY"
            cy="fullscreen-btn"
            @click.stop="
              (evt) => {
                emits('fullscreenBtnClick', evt)
              }
            "
          />
          <p-space
            v-if="isFullscreenButtonVisible && isDeleteButtonVisible"
            :mobile="PSpaceSize.XS"
          />
          <p-btn-table
            v-if="isDeleteButtonVisible"
            icon="trash"
            :color="Color.PRIMARY"
            cy="delete-btn"
            @click.stop="
              (evt) => {
                emits('deleteBtnClick', evt)
              }
            "
          />
        </p-card>

        <div class="ds-file-preview__card--content p-text-center p-flex-1">
          <template v-if="!imageSrc">
            <p-icon :icon="iconByFileType" :color="Color.GREY_500" />
            <p-space :mobile="PSpaceSize.XS" vertical />
            <p-text-description :text="effectiveFileName" :color="Color.GREY_500" />
          </template>
        </div>
      </div>
    </p-card>

    <template v-if="hint">
      <p-space :mobile="PSpaceSize.XS" vertical />
      <p-text-description :text="hint" :color="Color.GREY_500" />
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/_brand.module.scss';
@import '../../assets/scss/shared/variables/ds.spacing';

$square-size: 128px;

.ds-file-preview {
  &__card {
    display: flex;
    flex-direction: column;
    height: $square-size;
    width: 100%;

    border: 2px dashed getColor('grey300');

    &--has-image {
      border: none;
    }

    &--square {
      max-width: $square-size;
    }

    &--btns {
      min-height: 32px;
    }
  }

  &__card-inner {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    border-radius: inherit;
  }

  &__image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__card--btns {
    position: relative;
  }

  &__card--content {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
}
</style>
